import React, { createContext, useCallback, useContext, useEffect, useMemo, useReducer } from 'react'

import { PAIR_CHART, PAIR_DATA, PAIRS_BULK, PAIRS_CURRENT, PAIRS_HISTORICAL_BULK } from './queries'

import { useEthPrice } from './GlobalData'

import { isAddress } from '@ethersproject/address'
import { useWeb3React } from '@web3-react/core'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { getApolloClientV2ByChain } from './apollo'
import { TRACKED_OVERRIDES_PAIRS, TRACKED_OVERRIDES_TOKENS } from './constants'
import { get2DayPercentChange, getBlocksFromTimestamps, getPercentChange, getTimestampsForChanges } from './utils'
import { updateNameData } from './utils/data'

const UPDATE = 'UPDATE'
// const UPDATE_PAIR_TXNS = 'UPDATE_PAIR_TXNS'
const UPDATE_CHART_DATA = 'UPDATE_CHART_DATA'
const UPDATE_TOP_PAIRS = 'UPDATE_TOP_PAIRS'
// const UPDATE_HOURLY_DATA = 'UPDATE_HOURLY_DATA'

dayjs.extend(utc)

function safeAccess(object: Record<string, any>, path: string[]) {
  return object
    ? path.reduce(
        (accumulator, currentValue) => (accumulator && accumulator[currentValue] ? accumulator[currentValue] : null),
        object
      )
    : null
}

interface PairsState {
  [pair: string]: any
}

type PairDataContextType = [
  state: PairsState,
  {
    update: (pairAddress: string, data: any) => void
    // updatePairTxns: (address: string, transactions: any[]) => void
    updateChartData: (address: string, chartData: any) => void
    updateTopPairs: (topPairs: any[]) => void
    // updateHourlyData: (address: string, hourlyData: any, timeWindow: number) => void
  }
]

const PairDataContext = createContext<PairDataContextType | null>(null)

function usePairDataContext(): PairDataContextType {
  const context = useContext(PairDataContext)
  if (!context) {
    throw new Error('!PairDataContext')
  }
  return context
}

function reducer(state: PairsState, { type, payload }: { type: string; payload: any }) {
  switch (type) {
    case UPDATE: {
      const { pairAddress, data } = payload
      return {
        ...state,
        [pairAddress]: {
          ...state?.[pairAddress],
          ...data,
        },
      }
    }

    case UPDATE_TOP_PAIRS: {
      const { topPairs } = payload
      const added: Record<string, any> = {}
      topPairs.map((pair: any) => {
        return (added[pair.id] = pair)
      })
      return {
        ...state,
        ...added,
      }
    }

    // case UPDATE_PAIR_TXNS: {
    //   const { address, transactions } = payload
    //   return {
    //     ...state,
    //     [address]: {
    //       ...(safeAccess(state, [address]) || {}),
    //       txns: transactions,
    //     },
    //   }
    // }
    case UPDATE_CHART_DATA: {
      const { address, chartData } = payload
      return {
        ...state,
        [address]: {
          ...(safeAccess(state, [address]) || {}),
          chartData,
        },
      }
    }

    // case UPDATE_HOURLY_DATA: {
    //   const { address, hourlyData, timeWindow } = payload
    //   return {
    //     ...state,
    //     [address]: {
    //       ...state?.[address],
    //       hourlyData: {
    //         ...state?.[address]?.hourlyData,
    //         [timeWindow]: hourlyData,
    //       },
    //     },
    //   }
    // }

    default: {
      throw Error(`Unexpected action type in DataContext reducer: '${type}'.`)
    }
  }
}

export default function Provider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(reducer, {})

  // update pair specific data
  const update = useCallback((pairAddress: string, data: any) => {
    dispatch({
      type: UPDATE,
      payload: {
        pairAddress: pairAddress.toLowerCase(),
        data,
      },
    })
  }, [])

  const updateTopPairs = useCallback((topPairs: any[]) => {
    dispatch({
      type: UPDATE_TOP_PAIRS,
      payload: {
        topPairs,
      },
    })
  }, [])

  // const updatePairTxns = useCallback((address: string, transactions: any[]) => {
  //   dispatch({
  //     type: UPDATE_PAIR_TXNS,
  //     payload: { address, transactions },
  //   })
  // }, [])

  const updateChartData = useCallback((address: string, chartData: any) => {
    dispatch({
      type: UPDATE_CHART_DATA,
      payload: { address: address.toLowerCase(), chartData },
    })
  }, [])

  // const updateHourlyData = useCallback((address, hourlyData, timeWindow) => {
  //   dispatch({
  //     type: UPDATE_HOURLY_DATA,
  //     payload: { address, hourlyData, timeWindow },
  //   })
  // }, [])

  return (
    <PairDataContext.Provider
      value={useMemo(
        () => [
          state,
          {
            update,
            // updatePairTxns,
            updateChartData,
            updateTopPairs,
            // updateHourlyData,
          },
        ],
        // [state, update, updatePairTxns, updateChartData, updateTopPairs, updateHourlyData]
        [state, update, updateChartData, updateTopPairs]
      )}
    >
      {children}
    </PairDataContext.Provider>
  )
}

async function getBulkPairData(
  chainId: number,
  pairList: any[],
  ethPrice: number | undefined
): Promise<any[] | undefined> {
  const client = getApolloClientV2ByChain(chainId)

  if (!client) {
    return undefined
  }

  const [t1, t2, tWeek] = getTimestampsForChanges()
  const [{ number: b1 } = {} as any, { number: b2 } = {} as any, { number: bWeek } = {} as any] =
    await getBlocksFromTimestamps(chainId, [t1, t2, tWeek])

  let oneDayResult: any
  let twoDayResult: any
  let oneWeekResult: any

  try {
    ;[oneDayResult, twoDayResult, oneWeekResult] = await Promise.all(
      [b1, b2, bWeek].map(async (block) => {
        if (!block) {
          return undefined
        }
        const result = client.query({
          query: PAIRS_HISTORICAL_BULK(block, pairList),
          fetchPolicy: 'cache-first',
        })
        return result
      })
    )
  } catch (e) {
    console.log(e)
  }

  const oneDayData = oneDayResult?.data?.pairs.reduce((obj: Record<string, any>, cur: Record<string, any>) => {
    return { ...obj, [cur.id]: cur }
  }, {})

  const twoDayData = twoDayResult?.data?.pairs.reduce((obj: Record<string, any>, cur: Record<string, any>) => {
    return { ...obj, [cur.id]: cur }
  }, {})

  const oneWeekData = oneWeekResult?.data?.pairs.reduce((obj: Record<string, any>, cur: Record<string, any>) => {
    return { ...obj, [cur.id]: cur }
  }, {})

  try {
    const current = await client.query({
      query: PAIRS_BULK,
      variables: {
        allPairs: pairList,
      },
      fetchPolicy: 'cache-first',
    })

    const pairData = await Promise.all(
      current &&
        current.data.pairs.map(async (pair: any) => {
          let data = JSON.parse(JSON.stringify(pair)) // fixed: can't define property "x": "obj" is not extensible
          let oneDayHistory = oneDayData?.[pair.id]
          if (!oneDayHistory) {
            const newData = await client
              .query({
                query: PAIR_DATA(pair.id, b1),
                fetchPolicy: 'cache-first',
              })
              .catch(() => undefined)
            oneDayHistory = newData?.data.pairs[0]
          }
          let twoDayHistory = twoDayData?.[pair.id]
          if (!twoDayHistory) {
            const newData = await client
              .query({
                query: PAIR_DATA(pair.id, b2),
                fetchPolicy: 'cache-first',
              })
              .catch(() => undefined)
            twoDayHistory = newData?.data.pairs[0]
          }
          let oneWeekHistory = oneWeekData?.[pair.id]
          if (!oneWeekHistory) {
            const newData = await client
              .query({
                query: PAIR_DATA(pair.id, bWeek),
                fetchPolicy: 'cache-first',
              })
              .catch(() => undefined)
            oneWeekHistory = newData?.data.pairs[0]
          }
          data = parseData(data, oneDayHistory, twoDayHistory, oneWeekHistory, ethPrice, b1)
          return data
        })
    )
    return pairData
  } catch (e) {
    console.log(e)
  }

  return undefined
}

interface ParseDataResult extends Record<string, any> {
  oneDayVolumeUSD: number
  oneWeekVolumeUSD: number
  volumeChangeUSD: number
  oneDayVolumeUntracked: number
  oneWeekVolumeUntracked: number
  volumeChangeUntracked: number
  trackedReserveUSD: number
  liquidityChangeUSD: number
}

function parseData(
  rawData: Record<string, any>,
  oneDayData: any | undefined,
  twoDayData: any | undefined,
  oneWeekData: any | undefined,
  ethPrice: number = 0,
  oneDayBlock: number = 0
): ParseDataResult {
  const data = Object.assign({}, rawData)
  const pairAddress: string = data.id

  // get volume changes
  const [oneDayVolumeUSD, volumeChangeUSD] = get2DayPercentChange(
    data.volumeUSD,
    oneDayData?.volumeUSD ? oneDayData.volumeUSD : 0,
    twoDayData?.volumeUSD ? twoDayData.volumeUSD : 0
  )
  const [oneDayVolumeUntracked, volumeChangeUntracked] = get2DayPercentChange(
    data.untrackedVolumeUSD,
    oneDayData?.untrackedVolumeUSD ? parseFloat(oneDayData.untrackedVolumeUSD) : 0,
    twoDayData?.untrackedVolumeUSD ? twoDayData.untrackedVolumeUSD : 0
  )

  const oneWeekVolumeUSD = parseFloat(oneWeekData ? data.volumeUSD - oneWeekData?.volumeUSD : data.volumeUSD)

  const oneWeekVolumeUntracked = parseFloat(
    oneWeekData ? data.untrackedVolumeUSD - oneWeekData?.untrackedVolumeUSD : data.untrackedVolumeUSD
  )

  // set volume properties
  // data.oneDayVolumeUSD = parseFloat(oneDayVolumeUSD)
  data.oneDayVolumeUSD = oneDayVolumeUSD
  data.oneWeekVolumeUSD = oneWeekVolumeUSD
  data.volumeChangeUSD = volumeChangeUSD
  data.oneDayVolumeUntracked = oneDayVolumeUntracked
  data.oneWeekVolumeUntracked = oneWeekVolumeUntracked
  data.volumeChangeUntracked = volumeChangeUntracked

  // set liquidity properties
  data.trackedReserveUSD = data.trackedReserveETH * ethPrice
  data.liquidityChangeUSD = getPercentChange(data.reserveUSD, oneDayData?.reserveUSD)

  // format if pair hasnt existed for a day or a week
  if (!oneDayData && data && data.createdAtBlockNumber > oneDayBlock) {
    data.oneDayVolumeUSD = parseFloat(data.volumeUSD)
  }
  if (!oneDayData && data) {
    data.oneDayVolumeUSD = parseFloat(data.volumeUSD)
  }
  if (!oneWeekData && data) {
    data.oneWeekVolumeUSD = parseFloat(data.volumeUSD)
  }

  if (
    TRACKED_OVERRIDES_PAIRS.includes(pairAddress) ||
    TRACKED_OVERRIDES_TOKENS.includes(data.token0.id) ||
    TRACKED_OVERRIDES_TOKENS.includes(data.token1.id)
  ) {
    data.oneDayVolumeUSD = oneDayVolumeUntracked
    data.oneWeekVolumeUSD = oneWeekVolumeUntracked
    data.volumeChangeUSD = volumeChangeUntracked
    data.trackedReserveUSD = data.reserveUSD
  }

  // format incorrect names
  updateNameData(data)

  return data as ParseDataResult
}

// const getPairTransactions = async (pairAddress) => {
//   const transactions = {}
//
//   try {
//     const result = await client.query({
//       query: FILTERED_TRANSACTIONS,
//       variables: {
//         allPairs: [pairAddress],
//       },
//       fetchPolicy: 'no-cache',
//     })
//     transactions.mints = result.data.mints
//     transactions.burns = result.data.burns
//     transactions.swaps = result.data.swaps
//   } catch (e) {
//     console.log(e)
//   }
//
//   return transactions
// }

const getPairChartData = async (chainId: number, pairAddress: string) => {
  const client = getApolloClientV2ByChain(chainId)

  if (!client) {
    return undefined
  }

  let data: any[] = []
  const utcEndTime = dayjs.utc()
  const utcStartTime = utcEndTime.subtract(1, 'year').startOf('minute')
  const startTime = utcStartTime.unix() - 1

  try {
    let allFound = false
    let skip = 0
    while (!allFound) {
      const result = await client.query({
        query: PAIR_CHART,
        variables: {
          pairAddress,
          skip,
        },
        fetchPolicy: 'cache-first',
      })
      skip += 1000
      // Make a copy of the object so we can mutate it.
      const pairDayDatas = JSON.parse(JSON.stringify(result.data.pairDayDatas))
      data = data.concat(pairDayDatas)
      if (pairDayDatas.length < 1000) {
        allFound = true
      }
    }

    const dayIndexSet = new Set()
    const dayIndexArray: any[] = []
    const oneDay = 24 * 60 * 60
    data.forEach((dayData, i) => {
      // add the day index to the set of days
      dayIndexSet.add((data[i].date / oneDay).toFixed(0))
      dayIndexArray.push(data[i])
      dayData.dailyVolumeUSD = parseFloat(dayData.dailyVolumeUSD)
      dayData.reserveUSD = parseFloat(dayData.reserveUSD)
    })

    if (data[0]) {
      // fill in empty days
      let timestamp = data[0].date ? data[0].date : startTime
      let latestLiquidityUSD = data[0].reserveUSD
      let index = 1
      while (timestamp < utcEndTime.unix() - oneDay) {
        const nextDay = timestamp + oneDay
        const currentDayIndex = (nextDay / oneDay).toFixed(0)
        if (!dayIndexSet.has(currentDayIndex)) {
          data.push({
            date: nextDay,
            dayString: nextDay,
            dailyVolumeUSD: 0,
            reserveUSD: latestLiquidityUSD,
          })
        } else {
          latestLiquidityUSD = dayIndexArray[index].reserveUSD
          index = index + 1
        }
        timestamp = nextDay
      }
    }

    data = data.sort((a, b) => (parseInt(a.date) > parseInt(b.date) ? 1 : -1))
  } catch (e) {
    console.log(e)
  }

  return data
}

// const getHourlyRateData = async (pairAddress, startTime, latestBlock) => {
//   try {
//     const utcEndTime = dayjs.utc()
//     let time = startTime
//
//     // create an array of hour start times until we reach current hour
//     const timestamps = []
//     while (time <= utcEndTime.unix() - 3600) {
//       timestamps.push(time)
//       time += 3600
//     }
//
//     // backout if invalid timestamp format
//     if (timestamps.length === 0) {
//       return []
//     }
//
//     // once you have all the timestamps, get the blocks for each timestamp in a bulk query
//     let blocks
//
//     blocks = await getBlocksFromTimestamps(timestamps, 100)
//
//     // catch failing case
//     if (!blocks || blocks?.length === 0) {
//       return []
//     }
//
//     if (latestBlock) {
//       blocks = blocks.filter((b) => {
//         return parseFloat(b.number) <= parseFloat(latestBlock)
//       })
//     }
//
//     const result = await splitQuery(HOURLY_PAIR_RATES, client, [pairAddress], blocks, 100)
//
//     // format token ETH price results
//     const values = []
//     for (const row in result) {
//       const timestamp = row.split('t')[1]
//       if (timestamp) {
//         values.push({
//           timestamp,
//           rate0: parseFloat(result[row]?.token0Price),
//           rate1: parseFloat(result[row]?.token1Price),
//         })
//       }
//     }
//
//     const formattedHistoryRate0 = []
//     const formattedHistoryRate1 = []
//
//     // for each hour, construct the open and close price
//     for (let i = 0; i < values.length - 1; i++) {
//       formattedHistoryRate0.push({
//         timestamp: values[i].timestamp,
//         open: parseFloat(values[i].rate0),
//         close: parseFloat(values[i + 1].rate0),
//       })
//       formattedHistoryRate1.push({
//         timestamp: values[i].timestamp,
//         open: parseFloat(values[i].rate1),
//         close: parseFloat(values[i + 1].rate1),
//       })
//     }
//
//     return [formattedHistoryRate0, formattedHistoryRate1]
//   } catch (e) {
//     console.log(e)
//     return [[], []]
//   }
// }

export function Updater() {
  const [, { updateTopPairs }] = usePairDataContext()
  const [ethPrice] = useEthPrice()
  const { chainId } = useWeb3React()

  useEffect(() => {
    async function getData() {
      const client = getApolloClientV2ByChain(chainId)
      if (!chainId || !client) {
        return
      }
      // get top pairs by reserves
      const {
        data: { pairs },
      } = await client.query({
        query: PAIRS_CURRENT,
        fetchPolicy: 'cache-first',
      })

      // format as array of addresses
      const formattedPairs = pairs.map((pair: any) => {
        return pair.id
      })

      // get data for every pair in list
      const topPairs = await getBulkPairData(chainId, formattedPairs, ethPrice)
      topPairs && updateTopPairs(topPairs)
    }

    ethPrice && getData()
  }, [chainId, ethPrice, updateTopPairs])

  return null
}

// export function useHourlyRateData(pairAddress, timeWindow) {
//   const [state, { updateHourlyData }] = usePairDataContext()
//   const chartData = state?.[pairAddress]?.hourlyData?.[timeWindow]
//   const [latestBlock] = useLatestBlocks()
//
//   useEffect(() => {
//     const currentTime = dayjs.utc()
//     const windowSize = timeWindow === timeframeOptions.MONTH ? 'month' : 'week'
//     const startTime =
//       timeWindow === timeframeOptions.ALL_TIME ? 1589760000 : currentTime.subtract(1, windowSize).startOf('hour').unix()
//
//     async function fetch() {
//       const data = await getHourlyRateData(pairAddress, startTime, latestBlock)
//       updateHourlyData(pairAddress, data, timeWindow)
//     }
//
//     if (!chartData) {
//       fetch()
//     }
//   }, [chartData, timeWindow, pairAddress, updateHourlyData, latestBlock])
//
//   return chartData
// }

// /**
//  * @todo
//  * store these updates to reduce future redundant calls
//  */
// export function useDataForList(pairList) {
//   const [state] = usePairDataContext()
//   const [ethPrice] = useEthPrice()
//
//   const [stale, setStale] = useState(false)
//   const [fetched, setFetched] = useState([])
//
//   // reset
//   useEffect(() => {
//     if (pairList) {
//       setStale(false)
//       setFetched()
//     }
//   }, [pairList])
//
//   useEffect(() => {
//     async function fetchNewPairData() {
//       let newFetched = []
//       let unfetched = []
//
//       pairList.map(async (pair) => {
//         let currentData = state?.[pair.id]
//         if (!currentData) {
//           unfetched.push(pair.id)
//         } else {
//           newFetched.push(currentData)
//         }
//       })
//
//       let newPairData = await getBulkPairData(
//         unfetched.map((pair) => {
//           return pair
//         }),
//         ethPrice
//       )
//       setFetched(newFetched.concat(newPairData))
//     }
//     if (ethPrice && pairList && pairList.length > 0 && !fetched && !stale) {
//       setStale(true)
//       fetchNewPairData()
//     }
//   }, [ethPrice, state, pairList, stale, fetched])
//
//   let formattedFetch =
//     fetched &&
//     fetched.reduce((obj, cur) => {
//       return { ...obj, [cur?.id]: cur }
//     }, {})
//
//   return formattedFetch
// }

/**
 * Get all the current and 24hr changes for a pair
 */
export function usePairData(pairAddress: string | undefined) {
  const { chainId } = useWeb3React()
  const [state, { update }] = usePairDataContext()
  const [ethPrice] = useEthPrice()
  const pairData = useMemo(() => pairAddress && state?.[pairAddress.toLowerCase()], [pairAddress, state])

  useEffect(() => {
    async function fetchData() {
      if (chainId && !pairData && pairAddress && isAddress(pairAddress)) {
        const data = await getBulkPairData(chainId, [pairAddress], ethPrice)
        data && update(pairAddress, data[0])
      }
    }
    if (ethPrice) {
      fetchData()
    }
  }, [pairAddress, pairData, update, ethPrice, chainId])

  return pairData || {}
}

// /**
//  * Get most recent txns for a pair
//  */
// export function usePairTransactions(pairAddress) {
//   const [state, { updatePairTxns }] = usePairDataContext()
//   const pairTxns = state?.[pairAddress]?.txns
//   useEffect(() => {
//     async function checkForTxns() {
//       if (!pairTxns) {
//         let transactions = await getPairTransactions(pairAddress)
//         updatePairTxns(pairAddress, transactions)
//       }
//     }
//     checkForTxns()
//   }, [pairTxns, pairAddress, updatePairTxns])
//   return pairTxns
// }

export function usePairChartData(pairAddress: string | undefined) {
  const { chainId } = useWeb3React()
  const [state, { updateChartData }] = usePairDataContext()
  const chartData = useMemo(
    () => (pairAddress ? state?.[pairAddress.toLowerCase()]?.chartData : undefined),
    [pairAddress, state]
  )

  useEffect(() => {
    async function checkForChartData() {
      if (chainId && pairAddress && !chartData) {
        const data = await getPairChartData(chainId, pairAddress)
        updateChartData(pairAddress, data)
      }
    }
    checkForChartData()
  }, [chainId, chartData, pairAddress, updateChartData])

  return chartData
}

/**
 * Get list of all pairs in Uniswap V2
 */
export function useAllPairData() {
  const [state] = usePairDataContext()
  return useMemo(() => state || {}, [state])
}
